import React, { useEffect, useState } from 'react';
import { Form, Input, Select, Button, Row, Col, Space } from 'antd';
import dayjs from 'dayjs';
import { patchRequest, ResponseDataType } from 'api';
import { toast } from 'react-toastify';
import { DatePicker, parseFlexibleDate } from 'Components';

export interface StudentEditDataType {
  studentId: number;
  name: string;
  dob?: string;
  gender: number;
  studentCode: string;
  cmnd?: string;
  bhytNum?: string;
  address?: string;
  email?: string;
  parentPhonenum?: string;
}

interface EditStudentProps {
  studentData: StudentEditDataType;
  onSuccess: () => void;
  onClose: () => void;
}

export const EditStudent: React.FC<EditStudentProps> = ({
  studentData,
  onSuccess,
  onClose,
}) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (studentData) {
      const identifier = studentData.cmnd || studentData.studentCode || studentData.bhytNum;
      form.setFieldsValue({
        name: studentData.name,
        dob: parseFlexibleDate(studentData.dob),
        gender: studentData.gender !== undefined && studentData.gender !== null ? Number(studentData.gender) : undefined,
        cmnd: identifier,
        bhytNum: identifier,
        parentPhonenum: studentData.parentPhonenum,
        email: studentData.email,
        address: studentData.address,
      });
    }
  }, [studentData, form]);

  const handleFinish = async (values: any) => {
    setSubmitting(true);
    try {
      const dobString = values.dob
        ? dayjs(values.dob).isValid()
          ? dayjs(values.dob).format('YYYY-MM-DD')
          : undefined
        : undefined;

      const identifier = values.cmnd?.trim();

      const payload = {
        studentId: studentData.studentId,
        name: values.name?.trim(),
        dob: dobString,
        gender: values.gender,
        studentCode: identifier,
        cmnd: identifier,
        bhytNum: identifier,
        parentPhonenum: values.parentPhonenum?.trim(),
        email: values.email?.trim(),
        address: values.address?.trim(),
      };

      const res: ResponseDataType<any> = await patchRequest('/cms/student', payload);
      if (res?.code === 200) {
        toast.success('Cập nhật thông tin học sinh thành công!');
        onSuccess();
        onClose();
      } else {
        toast.error(res?.msg || 'Cập nhật thông tin học sinh thất bại!');
      }
    } catch (err: any) {
      toast.error('Có lỗi xảy ra khi cập nhật học sinh');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Form form={form} layout="vertical" onFinish={handleFinish} style={{ marginTop: 16 }}>
      <Row gutter={16}>
        <Col xs={24} sm={12}>
          <Form.Item
            name="name"
            label="Họ và tên"
            rules={[{ required: true, message: 'Vui lòng nhập họ và tên' }]}
          >
            <Input placeholder="Nhập họ và tên học sinh" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            name="dob"
            label="Ngày sinh"
            rules={[{ required: true, message: 'Vui lòng chọn ngày sinh' }]}
          >
            <DatePicker
              style={{ width: '100%' }}
              placeholder="Chọn hoặc dán ngày sinh"
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col xs={24} sm={12}>
          <Form.Item
            name="gender"
            label="Giới tính"
            rules={[{ required: true, message: 'Vui lòng chọn giới tính' }]}
          >
            <Select
              placeholder="Chọn giới tính"
              options={[
                { value: 1, label: 'Nam' },
                { value: 0, label: 'Nữ' },
              ]}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item 
            name="cmnd" 
            label="Số CMND / CCCD / Mã định danh"
            rules={[
              { pattern: /^\d{12}$/, message: 'CCCD phải bao gồm đúng 12 chữ số' }
            ]}
          >
            <Input 
              placeholder="Nhập số CMND hoặc CCCD" 
              onChange={(e) => {
                form.setFieldsValue({ bhytNum: e.target.value });
              }}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col xs={24} sm={12}>
          <Form.Item 
            name="bhytNum" 
            label="Mã thẻ BHYT"
            extra={<span style={{ fontSize: 12, color: '#888' }}>Tự động đồng bộ theo số CCCD / Mã định danh</span>}
          >
            <Input disabled placeholder="Đồng bộ theo số CMND/CCCD" />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col xs={24} sm={12}>
          <Form.Item 
            name="parentPhonenum" 
            label="Số điện thoại phụ huynh"
            extra={<span style={{ fontSize: 12, color: '#888' }}>SĐT phụ huynh được quản lý tại mục Thông tin phụ huynh</span>}
          >
            <Input disabled placeholder="Chưa có SĐT phụ huynh" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            name="email"
            label="Email"
            rules={[{ type: 'email', message: 'Email không đúng định dạng' }]}
          >
            <Input placeholder="Nhập địa chỉ email" />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={24}>
          <Form.Item name="address" label="Địa chỉ">
            <Input.TextArea rows={2} placeholder="Nhập địa chỉ cư trú" />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item style={{ marginBottom: 0, textAlign: 'right', marginTop: 16 }}>
        <Space>
          <Button onClick={onClose} disabled={submitting}>
            Hủy
          </Button>
          <Button type="primary" htmlType="submit" loading={submitting}>
            Lưu thay đổi
          </Button>
        </Space>
      </Form.Item>
    </Form>
  );
};
